import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
} from "src/lib/constants";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  convertChannelName,
  convertPaymentSystem,
  extractBillingStatus,
  extractServiceName,
  formatCardNumber,
  getCurrencyInfo,
  getErrorMessage,
  getTransactionStatusStyle,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  BalanceDataType,
  BankCardDataType,
  IBillingTransaction,
  TransactionDataType,
} from "src/lib/constants/table-type";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import SafeImage from "src/hooks/validateImageUrl";

type TransactionsBillingPageProps = {
  user: User;
  onSelectedInvoiceData: any;
};

const TransactionsBillingPage: FC<TransactionsBillingPageProps> = ({
  user,
  onSelectedInvoiceData,
}) => {
  const navigate = useNavigate();
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<IBillingTransaction[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    type: "money-mover",
    txn_type:"deposit",
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const [total, setTotal] = useState(0);

  useEffect(() => {
    isMounted && getList({ id: user.id, params: searchParams });
  }, [isMounted, searchParams.current_page]);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({ ...searchParams, current_page: page });
  };

  // const onSelectUser = (userID: number) => {
  //   navigate(`/${isBuisiness ? "users-business" : "users"}/${String(userID)}`, {
  //     replace: true,
  //   });
  // };

  const { mutate: getList, isLoading } = useMutation(
    user.is_business
      ? client.users.getBillingBuisinessTransactions
      : client.users.getBillingIndividualTransactions,
    {
      onSuccess: (data: any) => {
        setTotal(data?.total);

        setDataSource(
          data.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            userId: item?.user?.id,
            email: item?.user?.email,
            fullName:
              `${item?.user?.user_data?.first_name || ""} ${
                item?.user?.user_data?.last_name || ""
              }`.trim() || "__",
            // transactionId: item?.mm_txn_id,
            // transactionId: item?.info_response?.service_params?.account || "__",
            transactionId:
              item?.info_response?.service_params?.account ??
              item?.info_response?.service_params?.phone ??
              item?.info_response?.service_params?.personal_number ??
              "__",
            fee: item?.fee,
            serviceName: item?.serviceName,
            serviceImage: item?.service?.service_image,
            serviceCategory:
              item?.service?.service_category?.cat_name?.[2] ?? "__",
            serviceCategoryImage: item?.service?.service_category?.cat_image,
            amount: item.amount,
            benefit: item?.accountant?.benefit,
            commission: item?.accountant?.commission,
            clientCommission: item?.accountant?.client_commission,
            currency: item?.to_currency_id,
            status: item.status,
            created_at: item.created_at,
            payment_reference: item?.user?.payment_reference ?? "__",
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const onSelectUser = (userID: number) => {
    navigate(
      `/${user?.is_business ? "users-business" : "users"}/${String(userID)}`,
      {
        replace: true,
      }
    );
  };

  const onViewInvoice = (key: number) => {
    const filterInvoices = dataSource.filter((item) => item.key === key);
    if (filterInvoices.length === 0) {
      return;
    }

    const selInvoice = filterInvoices[0];
    onSelectedInvoiceData(selInvoice);
    /*
    setInvoiceFromPage(routes.users);
    selInvoice.invoice_data &&
      navigate(routes.invoice, { state: { info: selInvoice } });
    */
  };

  const changeName = (name: string) => {
    return name === "cartubank" ? "Card" : name;
  };

  const columns: ColumnsType<IBillingTransaction> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User ID
        </p>
      ),
      dataIndex: "userId",
      className: "w-auto",
      key: "userId",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p
            onClick={() => onSelectUser(value)}
            className="font-manrope text-secondary text-[14px] sm:text-[14px] font-medium underline hover:text-primary hover:underline cursor-pointer"
          >
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Reference
        </p>
      ),
      dataIndex: "payment_reference",
      className: "w-auto",
      key: "payment_reference",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Full Name
        </p>
      ),
      dataIndex: "fullName",
      className: "w-auto",
      key: "fullName",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Category
        </p>
      ),
      dataIndex: "serviceCategory",
      className: "w-auto",
      key: "serviceCategory",
      render: (value, record) => (
        <div className="flex flex-row gap-2 items-center">
          <SafeImage
            src={record?.serviceCategoryImage}
            alt={value}
            className="w-8 h-8 rounded-full object-cover"
            fallback={
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center"></div>
            }
          />
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium whitespace-nowrap mr-4">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Service
        </p>
      ),
      dataIndex: "serviceName",
      className: "w-auto",
      key: "serviceName",
      render: (value, record) => (
        <div className="flex flex-row gap-2 items-center">
          {record?.serviceImage && (
            <img
              src={record?.serviceImage}
              alt={value}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium ">
            {extractServiceName(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Account Number
        </p>
      ),
      dataIndex: "transactionId",
      className: "w-auto",
      key: "transactionId",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      className: "w-auto",
      key: "amount",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Fee
        </p>
      ),
      dataIndex: "fee",
      className: "w-auto",
      key: "fee",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Benefit
        </p>
      ),
      dataIndex: "benefit",
      className: "w-auto",
      key: "benefit",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Commission
        </p>
      ),
      dataIndex: "commission",
      className: "w-auto",
      key: "commission",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Client Commission
        </p>
      ),
      dataIndex: "clientCommission",
      className: "w-auto",
      key: "clientCommission",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => {
        return (
          <div
            className={`bg-[${
              value === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
            }] inline-block py-[4px] px-[12px] rounded-[10px]`}
          >
            <p
              className={`font-manrope ${
                value === "sent_to_iban" ? "text-[#fff]" : "text-primary"
              } text-[12px] font-medium leading-none`}
            >
              {value && extractBillingStatus(value)}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p
            className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium"
            style={{ whiteSpace: "nowrap" }}
          >
            {/* {value} */}
            {moment(value).utcOffset(240).format("DD/MM/YYYY H:mm")}
            {/* {moment.unix(value).utcOffset(240).format("DD MMM YYYY H:mm [GMT]+4")} */}
          </p>
        </div>
      ),
    },
  ];

  const mobileColumns: ColumnsType<IBillingTransaction> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Transaction History
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-2 justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Account Number
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.id}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Reference
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData ? String(selData?.payment_reference) : "-"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  UserId
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.userId || "---"}
                </p>
              </div>
              {/* <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Currency
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div> */}
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Full Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.fullName || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Category
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.serviceCategory || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Service
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.serviceName || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Amount
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.amount || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Benefit
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.benefit}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Commission
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.commission || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Client Commission
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.clientCommission || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Status
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {extractBillingStatus(selData.status)}
                </p>
              </div>
              {/* <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Status
                </p>
                <div
                  className={`bg-[${
                    selData.status === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
                  }] inline-block py-[4px] px-[12px] rounded-[10px]`}
                >
                  <p
                    className={`font-manrope ${
                      selData.status === "sent_to_iban"
                        ? "text-[#fff]"
                        : "text-primary"
                    } text-[12px] font-medium leading-none`}
                  >
                    {selData.status &&
                      capitalizeFirstLetter(
                        selData.status.replaceAll("_", " ")
                      )}
                  </p>
                </div>
              </div> */}
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Created
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {/* {moment(selData.created_at).format("YYYY-MM-DD HH:mm:ss")} */}
                  {moment(selData.created_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")}
                  /
                </p>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full">
          <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? mobileColumns : columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default TransactionsBillingPage;
